import { Accessor, createSignal } from "solid-js";

import { Channel, Client } from "stoat.js";

import { SoundController } from "@revolt/client";

export type IncomingCall = {
  channelId: string;
  callerId?: string;
  node?: string;
  token?: string;
};

type VoiceController = {
  connect(
    channel: Channel,
    auth?: { url: string; token: string },
  ): Promise<void>;
};

const [pendingCall, setPendingCallInternal] = createSignal<IncomingCall>();

let soundController: SoundController | undefined;

/**
 * Bind sound controller for ringtone playback (set once from IncomingCallHandler).
 */
export function bindIncomingCallSound(sound: SoundController) {
  soundController = sound;
}

export { pendingCall };

export function isIncomingForChannel(channelId: string): boolean {
  return pendingCall()?.channelId === channelId;
}

export function setIncomingCall(
  call: IncomingCall,
  options?: { playSound?: boolean },
) {
  const existing = pendingCall();
  if (existing?.channelId === call.channelId) {
    setPendingCallInternal({ ...existing, ...call });
    return;
  }

  setPendingCallInternal(call);

  if (options?.playSound !== false && soundController) {
    soundController.unlock();
    soundController.playSound("ringtoneIncoming", true);
  }
}

export function clearIncomingCall() {
  // Stop only the incoming ringtone so unrelated UI sounds keep working.
  if (soundController?.stopRingtone) {
    soundController.stopRingtone();
  } else {
    soundController?.stopSound();
  }
  setPendingCallInternal(undefined);
}

export async function acceptIncomingCall(
  call: IncomingCall,
  deps: { client: Client; voice: VoiceController },
) {
  const channel = deps.client.channels.get(call.channelId);
  if (!channel) return;

  clearIncomingCall();

  if (call.node && call.token) {
    await deps.voice.connect(channel, { url: call.node, token: call.token });
  } else {
    await deps.voice.connect(channel);
  }
}

export function declineIncomingCall() {
  clearIncomingCall();
}

export function pendingCaller(
  client: Client,
  call: Accessor<IncomingCall | undefined>,
) {
  const pending = call();
  if (!pending?.callerId) return undefined;
  return client.users.get(pending.callerId);
}
